import { useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';

interface AiCfg {
  baseUrl: string;
  apiKey: string;
  model: string;
  enabled: boolean;
  frequency: string;
  topic: string;
  prompt: string;
  /** direct=先发后审；review=先审后发 */
  publishMode: 'direct' | 'review';
}

interface Row {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  status: string;
  source: string;
  aiModel: string | null;
  slot: string | null;
  viewCount: number;
  pinned: boolean;
  createdAt: number;
}

const FREQ: { v: string; t: string }[] = [
  { v: 'hourly', t: '每 1 小时' },
  { v: '6h', t: '每 6 小时' },
  { v: 'daily', t: '每天 1 更（推荐）' },
  { v: '2d', t: '两天 1 更' },
  { v: 'weekly', t: '每周 1 更' },
];

const STATUS_TEXT: Record<string, string> = {
  draft: '草稿',
  pending: '待审',
  published: '已发布',
  taken_down: '已下架',
};

/** 后台：AI SEO 栏目（全自动生成 + 先发后审 + 文章管理） */
export default function AdminSeo() {
  const [cfg, setCfg] = useState<AiCfg | null>(null);
  const [items, setItems] = useState<Row[]>([]);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [c, a] = await Promise.all([
      api<{ config: AiCfg }>('/admin/ai-settings'),
      api<{ items: Row[] }>('/admin/articles'),
    ]);
    if (c.ok && c.data) setCfg(c.data.config);
    if (a.ok && a.data) setItems(a.data.items);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function saveCfg() {
    if (!cfg) return;
    const res = await api('/admin/ai-settings', {
      method: 'PUT',
      body: JSON.stringify(cfg),
    });
    setNotice(res.ok ? 'AI 设置已保存' : `保存失败：${errMsg(res, '未知错误')}`);
  }

  async function generate() {
    setBusy(true);
    const res = await api<{
      ok?: boolean;
      id?: string;
      slot?: string;
      skipped?: string;
      error?: string;
    }>('/admin/articles/generate', {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
    setBusy(false);

    const d = res.data;
    if (!res.ok) setNotice(`生成失败：${errMsg(res, '未知错误')}`);
    else if (d?.ok) setNotice(`已生成文章（槽位 ${d.slot ?? '-'}）`);
    else setNotice(`未生成：${d?.skipped || d?.error || '未知原因'}`);
    load();
  }

  async function patch(id: string, body: Record<string, unknown>) {
    const res = await api(`/admin/articles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    setNotice(res.ok ? '已更新' : `操作失败：${errMsg(res, '未知错误')}`);
    load();
  }

  async function remove(r: Row) {
    if (!window.confirm(`确定删除文章「${r.title}」？`)) return;
    const res = await api(`/admin/articles/${r.id}`, { method: 'DELETE' });
    setNotice(res.ok ? '已删除' : `删除失败：${errMsg(res, '未知错误')}`);
    load();
  }

  if (loading) return <p className="text-sm text-ink-500">加载中…</p>;

  return (
    <div>
      {notice && (
        <p className="mb-4 rounded-lg bg-candle-300/20 px-4 py-2.5 text-sm text-ink-700">
          {notice}
        </p>
      )}

      {/* AI 网关设置 */}
      <h2 className="mb-3 text-sm font-medium text-ink-900">AI 网关设置</h2>
      {cfg && (
        <div className="mb-6 rounded-xl border border-ink-200 bg-ink-50/60 p-4">
          <label className="mb-3 flex items-center gap-2 text-xs text-ink-700">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            />
            启用全自动 AI 生成（关闭后 Cron 不会写文章）
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">网关地址</span>
              <input
                value={cfg.baseUrl}
                onChange={(e) => setCfg({ ...cfg, baseUrl: e.target.value })}
                placeholder="https://ai-api.9r.pw/v1"
                className="h-9 w-full rounded-md border border-ink-200 bg-white px-2 text-xs outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">模型</span>
              <input
                value={cfg.model}
                onChange={(e) => setCfg({ ...cfg, model: e.target.value })}
                placeholder="gpt-4o-mini"
                className="h-9 w-full rounded-md border border-ink-200 bg-white px-2 text-xs outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">API KEY</span>
              <input
                type="password"
                value={cfg.apiKey}
                onChange={(e) => setCfg({ ...cfg, apiKey: e.target.value })}
                placeholder="后台填写，不入库明文展示"
                className="h-9 w-full rounded-md border border-ink-200 bg-white px-2 text-xs outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">生成频率</span>
              <select
                value={cfg.frequency}
                onChange={(e) => setCfg({ ...cfg, frequency: e.target.value })}
                className="h-9 w-full rounded-md border border-ink-200 bg-white px-2 text-xs outline-none"
              >
                {FREQ.map((f) => (
                  <option key={f.v} value={f.v}>
                    {f.t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">生成后处置</span>
              <select
                value={cfg.publishMode ?? 'review'}
                onChange={(e) =>
                  setCfg({ ...cfg, publishMode: e.target.value as 'direct' | 'review' })
                }
                className="h-9 w-full rounded-md border border-ink-200 bg-white px-2 text-xs outline-none"
              >
                <option value="review">先审后发（进审核队列，后台放行后公开）</option>
                <option value="direct">先发后审（生成即公开，事后可下架）</option>
              </select>
            </label>
          </div>

          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] text-ink-600">站点主题（AI 围绕它写）</span>
            <input
              value={cfg.topic}
              onChange={(e) => setCfg({ ...cfg, topic: e.target.value })}
              placeholder="如：缅怀与纪念、英雄纪念堂"
              className="h-9 w-full rounded-md border border-ink-200 bg-white px-2 text-xs outline-none"
            />
          </label>

          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] text-ink-600">提示词模板</span>
            <textarea
              value={cfg.prompt}
              onChange={(e) => setCfg({ ...cfg, prompt: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none"
            />
          </label>

          <button
            onClick={saveCfg}
            className="mt-3 rounded-full bg-ink-900 px-4 py-1.5 text-xs text-white"
          >
            保存 AI 设置
          </button>
        </div>
      )}

      {/* 手动生成 */}
      <h2 className="mb-3 text-sm font-medium text-ink-900">立即生成</h2>
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-ink-200 bg-white p-4">
        <label className="flex items-center gap-1.5 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
          />
          忽略槽位幂等（强制再生成一篇）
        </label>
        <button
          onClick={generate}
          disabled={busy}
          className="rounded-full bg-ink-900 px-4 py-1.5 text-xs text-white disabled:opacity-50"
        >
          {busy ? '生成中…' : '立即生成一篇'}
        </button>
        <span className="text-[11px] text-ink-500">
          生成即发布（先发后审），可随时下架
        </span>
      </div>

      {/* 文章管理 */}
      <h2 className="mb-3 text-sm font-medium text-ink-900">平台级文章（{items.length}）</h2>
      {items.length === 0 ? (
        <p className="text-xs text-ink-500">还没有文章，可先填好 AI 设置后点「立即生成一篇」</p>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => (
            <li
              key={r.id}
              className="flex items-start gap-2 rounded-lg border border-ink-200 bg-white p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink-900">{r.title}</p>
                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-ink-500">
                  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-ink-700">
                    {STATUS_TEXT[r.status] ?? r.status}
                  </span>
                  <span>{r.source === 'ai' ? 'AI' : '人工'}</span>
                  {r.slot && <span>槽位 {r.slot}</span>}
                  <span>浏览 {r.viewCount}</span>
                  <span>{new Date(r.createdAt * 1000).toLocaleDateString('zh-CN')}</span>
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-1.5">
                {r.status === 'published' ? (
                  <button
                    onClick={() => patch(r.id, { status: 'taken_down' })}
                    className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] text-amber-700"
                  >
                    下架
                  </button>
                ) : (
                  <button
                    onClick={() => patch(r.id, { status: 'published' })}
                    className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] text-emerald-700"
                  >
                    恢复
                  </button>
                )}
                <button
                  onClick={() => patch(r.id, { pinned: !r.pinned })}
                  className="rounded-full bg-ink-100 px-2.5 py-1 text-[10px] text-ink-700"
                >
                  {r.pinned ? '取消置顶' : '置顶'}
                </button>
                <button
                  onClick={() => remove(r)}
                  className="rounded-full bg-red-50 px-2.5 py-1 text-[10px] text-red-600"
                >
                  删除
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-6 text-[11px] leading-relaxed text-ink-400">
        SEO：文章由 Worker 服务端渲染，可直接被搜索引擎与 AI 抓取。
        <br />
        列表 <code>/articles</code> · 详情 <code>/articles/{"{slug}"}</code> ·{' '}
        <code>/sitemap.xml</code> · <code>/rss.xml</code> · <code>/robots.txt</code> ·{' '}
        <code>/llms.txt</code>
      </p>
    </div>
  );
}
